/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * STATUS DE ASSINATURA — TRADUÇÃO E OPÇÕES DE FILTRO
 *
 * Fonte única para tabela, cards, filtro e exportação. Antes cada tela tinha o
 * seu próprio mapa e eles divergiam:
 *
 *  · 'past_due' não existia em nenhum deles, então caía no `default` e aparecia
 *    cru na tela como "past_due" — e não tinha card nem opção de filtro. O
 *    produtor somava os cards, não fechava com o total e concluía que faltavam
 *    assinaturas (reclamação real, out/2026).
 *
 *  · os filtros ofereciam 'suspended', 'pending' e 'expired', que NÃO EXISTEM no
 *    banco: três das cinco opções nunca retornavam nada.
 *
 * Os únicos status que o backend grava são: active, canceled, past_due e
 * canceling (conferido na base: 3.566 / 937 / 304 / 1).
 * ═══════════════════════════════════════════════════════════════════════════════
 */

export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'canceling';

type Definicao = {
    label: string;
    /** cor do componente Badge (success | error | warning | info) */
    badge: 'success' | 'error' | 'warning' | 'info';
    /** cor usada pelo badge da tela de detalhe */
    cor: 'green' | 'red' | 'yellow' | 'gray';
    descricao: string;
};

const STATUS: Record<string, Definicao> = {
    active: {
        label: 'Ativa',
        badge: 'success',
        cor: 'green',
        descricao: 'Em dia: o próximo ciclo ainda não venceu.',
    },
    canceled: {
        label: 'Cancelada',
        badge: 'error',
        cor: 'red',
        descricao: 'Encerrada. Não haverá nova cobrança.',
    },
    // 'past_due' é o termo técnico. Para o produtor, o que importa é que o ciclo
    // venceu e o pagamento não entrou — por recusa do cartão, ou porque é
    // PIX/boleto, que só renova se o cliente pagar de novo.
    past_due: {
        label: 'Renovação pendente',
        badge: 'warning',
        cor: 'yellow',
        descricao: 'O ciclo venceu e o pagamento não entrou.',
    },
    canceling: {
        label: 'Cancelamento agendado',
        badge: 'warning',
        cor: 'yellow',
        descricao: 'Cancelada ao fim do ciclo atual.',
    },
};

/** 'cancelled' (com dois L) aparece em dados antigos. */
const normalizar = (status?: string | null): string => {
    const s = String(status ?? '').toLowerCase().trim();
    return s === 'cancelled' ? 'canceled' : s;
};

/** Nome em português. Status desconhecido volta como veio, para não esconder dado novo. */
export const translateSubscriptionStatus = (status?: string | null): string => {
    const s = normalizar(status);
    if (!s) return '-';                       // null, undefined ou string vazia
    return STATUS[s]?.label ?? String(status);
};

export const subscriptionStatusBadge = (status?: string | null): 'success' | 'error' | 'warning' | 'info' =>
    STATUS[normalizar(status)]?.badge ?? 'info';

export const subscriptionStatusColor = (status?: string | null): 'green' | 'red' | 'yellow' | 'gray' =>
    STATUS[normalizar(status)]?.cor ?? 'gray';

export const subscriptionStatusDescription = (status?: string | null): string =>
    STATUS[normalizar(status)]?.descricao ?? '';

/**
 * A assinatura ainda pode ser cancelada pelo produtor?
 *
 * 'past_due' PRECISA aparecer: o ciclo venceu e o pagamento não entrou, que é
 * justamente quando o produtor quer encerrar. A tela só mostrava o botão para
 * 'active', então quem estava em renovação pendente ficava sem ação nenhuma —
 * mesmo problema que o 'paused' já tinha causado antes.
 *
 * O backend não tem trava de status no cancelamento; a restrição era só da tela.
 *
 * 'canceling' fica de fora porque o cancelamento já está em curso, e 'canceled'
 * porque já acabou.
 */
export const podeCancelarAssinatura = (status?: string | null): boolean => {
    const s = normalizar(status);
    return s === 'active' || s === 'past_due';
};

/** Opções do filtro: só o que o backend realmente grava. */
export const SUBSCRIPTION_STATUS_OPTIONS: Array<{ id: SubscriptionStatus; label: string }> = [
    { id: 'active', label: STATUS.active.label },
    { id: 'past_due', label: STATUS.past_due.label },
    { id: 'canceled', label: STATUS.canceled.label },
    { id: 'canceling', label: STATUS.canceling.label },
];
